import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { DrawerHeader, ModalProps, useCloseClassName } from '@features/Modal';
import { ISettingsAccountsDto } from '@shared/api';
import classNames from 'classnames';
import { Button, Input, Range, Select, SelectOption, Tooltip } from '@shared/UI';
import { HiOutlineInformationCircle } from 'react-icons/hi';
import { Steps } from '@features/Steps';
import { GeneralStep, PersonalInfoStep, FinancialProfileStep, EDocs } from '../';
import { BiEditAlt } from 'react-icons/bi';
import { putAccount } from '../api';
import { HandleRefresh } from '@shared/react/hooks/useAsyncData/interfaces';
import { getSelectValue } from './utils/getSelectValue';
import { AccountACH } from '../AccountACH';
import './AccountsViewDrawer.scss';

interface AccountsInfoProps {
    fieldName: string;
    value: string | undefined | null | boolean;
    shouldSplit?: boolean;
    editableField?: 'select' | 'input' | 'range';
    keyName?: string;
    options?: SelectOption[];
    onChange?: (key: string, value: string | boolean | number) => void;
    className?: string;
}

export const AccountsInfo: React.FC<AccountsInfoProps> = (props) => {
    const { fieldName, value, className, editableField, onChange, keyName, options } = props;

    const [openEdit, setOpenEdit] = useState(false);

    const renderEditableField = useMemo(() => {
        if (!openEdit || !keyName) {
            return value;
        }

        switch (editableField) {
            case 'input':
                return (
                    <Input
                        editable
                        value={value?.toString()}
                        onChange={(e) => onChange?.(keyName, e.target.value)}
                    />
                );
            case 'select':
                return (
                    <Select
                        currentValue={getSelectValue(fieldName, value ? value?.toString() : '').toString()}
                        onSelect={(value) => {
                            onChange?.(keyName!, value);
                        }}
                        options={options || []}
                    />
                );
            case 'range':
                return (
                    <Range
                        min={0}
                        max={100}
                        value={String(value)}
                        onNumberChange={(value) => {
                            onChange?.(keyName!, String(value));
                        }}
                    />
                );
            default:
                return value;
        }
    }, [openEdit, value]);

    return (
        <div className={classNames('widget-trade-status-table-drawer__info-item', className)}>
            <span
                data-tooltip-id={fieldName}
                className='widget-trade-status-table-drawer__info-header'
            >
                <HiOutlineInformationCircle id={fieldName} />
                <Tooltip
                    id={fieldName}
                    place={'bottom'}
                    content={`Some info about ${fieldName}`}
                    opacity={1}
                    style={{
                        backgroundColor: '#FFFFFF',
                        padding: 12,
                        borderRadius: 12,
                        fontSize: 12,
                        color: '#000000BF',
                        boxShadow: '0px 0px 6px -1px #0000001A',
                    }}
                />
                {fieldName}: {''}
            </span>
            <span className={classNames('widget-trade-status-table-drawer__info-content', { nullable: !value })}>
                {renderEditableField}
                {editableField && <BiEditAlt onClick={() => setOpenEdit((prev) => !prev)} />}
            </span>
        </div>
    );
};

interface AccountsViewModalProps extends ModalProps {
    account: ISettingsAccountsDto;
    accountsRefresh: HandleRefresh;
    VerificationModal?: FunctionComponent<any>;
    localStorageKey?: string;
}

export const AccountsViewDrawer: React.FC<AccountsViewModalProps> = (props) => {
    const { account, accountsRefresh, onClose, localStorageKey } = props;

    const [editedValues, setEditedValues] = useState<ISettingsAccountsDto>(account);
    const [showSaveBtn, setShowBtn] = useState(false);

    useEffect(() => {
        if (JSON.stringify(account) !== JSON.stringify(editedValues)) {
            localStorage.setItem(localStorageKey || '', 'true');
        } else {
            localStorage.removeItem(localStorageKey || '');
        }
        setShowBtn(JSON.stringify(account) !== JSON.stringify(editedValues));
    }, [editedValues]);

    useEffect(() => {
        return () => {
            localStorage.removeItem(localStorageKey || '');
        };
    }, []);

    const updateAccount = async () => {
        await putAccount(editedValues);
        localStorage.removeItem(localStorageKey || '');
        onClose?.();
        accountsRefresh();
    };

    useCloseClassName({ id: account.id.toString(), className: 'widget-trade-status-table-modal__close' });

    return (
        <div className='widget-trade-status-table-drawer'>
            <DrawerHeader headerTitle={`Account Details`} />
            <Steps
                titles={['General', 'Personal Info', 'Financial Profile', 'ACH Relationships', 'E-Docs']}
                footerClassName='widget-trade-status-table-drawer__footer'
                headerClassName='widget-trade-status-table-drawer__header'
                submitLabel='Save Changes'
                className='widget-trade-status-table-drawer__steps'
                footerVisible={false}
                headerStepLabelClassName='widget-trade-status-table-drawer__header-step-label'
                headerStepLineClassName='widget-trade-status-table-drawer__header-step-line'
            >
                <GeneralStep
                    editedValues={editedValues}
                    setEditedValues={setEditedValues}
                    account={account}
                    fromDetail={false}
                />
                <PersonalInfoStep
                    editedValues={editedValues}
                    setEditedValues={setEditedValues}
                    account={account}
                    fromDetail={false}
                />
                <FinancialProfileStep
                    editedValues={editedValues}
                    setEditedValues={setEditedValues}
                    account={account}
                    fromDetail={false}
                />
                <AccountACH accountID={account.id} />
                <EDocs clearingAccount={account.clearingAccount} />
            </Steps>
            <Button
                disabled={!showSaveBtn}
                onClick={updateAccount}
                className='widget-trade-status-table-drawer__save-btn'
            >
                Save changes
            </Button>
        </div>
    );
};
